/**
 * Inspections List Page.
 *
 * Lists inspections for the current organisation in a filterable DataTable,
 * mirroring the Claims list surface: dynamic status pills, the shared
 * compact toolbar (saved views + search + columns + record-metadata filter
 * fly-out), and the standard list-page header with a compact action button.
 *
 * A Priority | Pool landing split sits above the toolbar: Priority = the
 * unplanned world (claim-triggered / manual / API, most urgent first); Pool =
 * the bulk registration programme (pool-staged registrations). The split is
 * a server-side `bucket` scope on the list endpoint; the pills, facets, and
 * saved views all compute over whichever bucket is loaded.
 *
 * "Priority" and "Pool" are OUR words, not the client's - OC never supplied
 * their own terms for this split. The three tab labels are therefore read
 * through `t()` with a seeded default, so renaming them when the client does
 * have an opinion is a seed change rather than a code change.
 *
 * The list endpoint returns a flat array (up to 200 rows), so status, type,
 * result, and free-text search are all applied client-side over the loaded
 * set. Status pills are built from the loaded rows' lifecycle status counts;
 * type + result are exposed as facets in the filter fly-out.
 *
 * Gated by the `inspections` feature flag - tenants without the flag
 * do not render this route. The flag check at the AppShell route level
 * keeps them from even hitting this code path.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  BarChart3,
  CalendarDays,
  ClipboardCheck,
  Flag,
  ListChecks,
  Route,
  Wrench,
} from "lucide-react";
import { AppShell } from "@/admin/components/app-shell";
import { Button } from "@/shared/components/ui/button";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { Badge } from "@/shared/components/ui/badge";
import { DataTable, type DataTableColumn } from "@/admin/components/data-table";
import { ListPageToolbar } from "@/admin/components/list-page-toolbar";
import { EmptyState } from "@/admin/components/shared/empty-state";
import { NotificationDot } from "@/shared/components/notifications/notification-dot";
import { useTableColumnPreferences } from "@/admin/hooks/use-table-column-preferences";
import { useTableFilterPreferences } from "@/admin/hooks/use-table-filter-preferences";
import {
  useTableViews,
  useCreateTableView,
  useDeleteTableView,
  useUpdateTableView,
  useSetOrgDefaultView,
} from "@/admin/hooks/use-table-views";
import type { TableView, TableViewConfig } from "@/admin/types/table-views";
import type { StatusPillItem } from "@/admin/components/shared/status-pills";
import type { ProductFilterFacet } from "@/shared/components/ui/product-filter-menu";
import { cn } from "@/shared/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  useInspections,
  useInspectionChoices,
  useInspectionSubTypes,
  useBulkMarkInspectionPriority,
  type InspectionRow,
} from "@/admin/hooks/use-inspections";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { FeatureGate } from "@/admin/components/feature-gate";
import { FLAGS } from "@/admin/hooks/use-feature-flag";
import { useFormatters } from "@/shared/lib/formatting";

const TABLE_ID = "inspections";

function formatPropertyAddress(row: InspectionRow): string {
  const parts = [
    row.property_address_line_1,
    row.property_city,
    row.property_state,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "-";
}

function buildLabelMap(
  options: { value: string; label: string }[] | undefined,
): Record<string, string> {
  if (!options) return {};
  return Object.fromEntries(options.map((o) => [o.value, o.label]));
}

/** Midnight of the given date, in local time. */
function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Whole days from today's midnight to the given day's midnight (future = +). */
function dayOffset(iso: string): number {
  const today = startOfDay(new Date()).getTime();
  const day = startOfDay(new Date(iso)).getTime();
  return Math.round((day - today) / 86_400_000);
}

/** Created-date presets (past-oriented) shown in the filter fly-out. */
const CREATED_PRESETS: { value: string; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

function matchesCreatedPreset(iso: string, preset: string): boolean {
  const offset = dayOffset(iso);
  switch (preset) {
    case "today":
      return offset === 0;
    case "7d":
      return offset <= 0 && offset >= -7;
    case "30d":
      return offset <= 0 && offset >= -30;
    case "90d":
      return offset <= 0 && offset >= -90;
    default:
      return true;
  }
}

/** Scheduled-date presets (future-oriented, plus overdue/unscheduled). */
const SCHEDULED_PRESETS: { value: string; label: string }[] = [
  { value: "unscheduled", label: "Unscheduled" },
  { value: "overdue", label: "Overdue" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Next 7 days" },
  { value: "30d", label: "Next 30 days" },
];

function matchesScheduledPreset(iso: string | null, preset: string): boolean {
  if (preset === "unscheduled") return iso == null;
  if (iso == null) return false;
  const offset = dayOffset(iso);
  switch (preset) {
    case "overdue":
      return offset < 0;
    case "today":
      return offset === 0;
    case "7d":
      return offset >= 0 && offset <= 7;
    case "30d":
      return offset >= 0 && offset <= 30;
    default:
      return true;
  }
}

/** Client-side free-text match across the searchable fields of a row. */
function rowMatchesTerm(row: InspectionRow, term: string): boolean {
  return [
    row.code,
    row.sub_type_label,
    row.inspector_name,
    row.contractor_name,
    row.property_address_line_1,
    row.property_city,
    row.property_state,
  ].some((f) => (f ?? "").toLowerCase().includes(term));
}

export function InspectionsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const { can } = usePermissions();
  const canCreate = can("inspections.create");
  // Narrow codename - a Resolution Specialist holds it without holding the
  // coarse inspections.edit permission, so do not gate marking on edit.
  const canMarkPriority = can("inspections.mark_priority");
  // One hook instance for the whole table: the bulk endpoint takes a list, so
  // a single-row toggle is just a list of one. Avoids a hook per row.
  const markPriority = useBulkMarkInspectionPriority();

  const [search, setSearch] = useState("");
  // Landing split: Priority = unplanned work (claim/manual/API triggered, most
  // urgent first); Pool = the bulk registration programme. Server-side scope
  // on the list endpoint; the pills/facets/views compute over the loaded set.
  const [bucket, setBucket] = useState<"all" | "priority" | "pool">("all");
  // Status pill selection (a lifecycle_status value) and the record-metadata
  // filter fly-out selection (type + result facets), persisted per tenant+table.
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [filterValues, setFilterValues] = useTableFilterPreferences(TABLE_ID);
  const [activeViewUuid, setActiveViewUuid] = useState<string | null>(null);

  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    saveColumnVisibility,
    saveColumnOrder,
    applyDefaultColumnPreferences,
  } = useTableColumnPreferences(TABLE_ID);

  // Single bucket-scoped fetch drives both the pills and the rows; every other
  // filter is applied client-side over the loaded set (the endpoint caps at 200).
  const {
    data: inspections,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useInspections({
    bucket: bucket === "all" ? undefined : bucket,
  });
  const { data: choices } = useInspectionChoices();
  const { data: subTypes } = useInspectionSubTypes();
  const resultLabels = useMemo(
    () => buildLabelMap(choices?.results),
    [choices],
  );
  const statusLabels = useMemo(
    () => buildLabelMap(choices?.lifecycle_statuses),
    [choices],
  );

  // Saved views (generic, table_id-scoped - same infra as Claims).
  const { data: views = [] } = useTableViews(TABLE_ID);
  const createView = useCreateTableView();
  const deleteView = useDeleteTableView();
  const updateView = useUpdateTableView();
  const setOrgDefault = useSetOrgDefaultView();

  const term = search.trim().toLowerCase();
  const subTypeSelection = useMemo(
    () => filterValues["sub_type"] ?? [],
    [filterValues],
  );
  const resultSelection = useMemo(
    () => filterValues["result"] ?? [],
    [filterValues],
  );
  const inspectorSelection = useMemo(
    () => filterValues["inspector"] ?? [],
    [filterValues],
  );
  const stateSelection = useMemo(
    () => filterValues["state"] ?? [],
    [filterValues],
  );
  const statusSelection = useMemo(
    () => filterValues["status"] ?? [],
    [filterValues],
  );
  const createdPreset = useMemo(
    () => filterValues["created"]?.[0],
    [filterValues],
  );
  const scheduledPreset = useMemo(
    () => filterValues["scheduled"]?.[0],
    [filterValues],
  );

  // Rows filtered by every fly-out facet EXCEPT the lifecycle-status ones (the
  // status pill + the status facet) - this is the set the pills count over, so
  // a pill's count reflects the other active filters (mirrors the Claims list,
  // where pills sit above the state filter).
  const preStatusRows = useMemo(() => {
    const all = inspections ?? [];
    return all.filter((row) => {
      if (
        subTypeSelection.length &&
        !subTypeSelection.includes(row.sub_type_key)
      )
        return false;
      if (resultSelection.length && !resultSelection.includes(row.result))
        return false;
      if (inspectorSelection.length) {
        const key = row.inspector_email ?? "none";
        if (!inspectorSelection.includes(key)) return false;
      }
      if (stateSelection.length) {
        const key = row.property_state || "none";
        if (!stateSelection.includes(key)) return false;
      }
      if (createdPreset && !matchesCreatedPreset(row.created_at, createdPreset))
        return false;
      if (
        scheduledPreset &&
        !matchesScheduledPreset(row.scheduled_for, scheduledPreset)
      )
        return false;
      if (term && !rowMatchesTerm(row, term)) return false;
      return true;
    });
  }, [
    inspections,
    subTypeSelection,
    resultSelection,
    inspectorSelection,
    stateSelection,
    createdPreset,
    scheduledPreset,
    term,
  ]);

  // The status pill and the fly-out status facet both filter lifecycle_status
  // and combine (AND): a row must satisfy both when both are set.
  const rows = useMemo(() => {
    return preStatusRows.filter((r) => {
      if (statusFilter && r.lifecycle_status !== statusFilter) return false;
      if (
        statusSelection.length &&
        !statusSelection.includes(r.lifecycle_status)
      )
        return false;
      return true;
    });
  }, [preStatusRows, statusFilter, statusSelection]);

  const statusPills: StatusPillItem[] = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of preStatusRows) {
      counts.set(r.lifecycle_status, (counts.get(r.lifecycle_status) ?? 0) + 1);
    }
    return Array.from(counts).map(([key, count]) => ({
      key,
      label: statusLabels[key] ?? key,
      count,
    }));
  }, [preStatusRows, statusLabels]);

  // Unique inspectors present in the loaded set, plus an Unassigned bucket, so
  // the fly-out can filter by who is assigned without a separate roster fetch.
  const inspectorOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    let hasUnassigned = false;
    for (const row of inspections ?? []) {
      if (row.inspector_email) {
        byKey.set(
          row.inspector_email,
          row.inspector_name || row.inspector_email,
        );
      } else {
        hasUnassigned = true;
      }
    }
    const options = Array.from(byKey, ([value, label]) => ({
      value,
      label,
    })).sort((a, b) => a.label.localeCompare(b.label));
    if (hasUnassigned) options.push({ value: "none", label: "Unassigned" });
    return options;
  }, [inspections]);

  // Unique property states present in the loaded set, plus an Unknown bucket,
  // so the fly-out can filter by the location's state without a lookup fetch.
  const stateOptions = useMemo(() => {
    const values = new Set<string>();
    let hasBlank = false;
    for (const row of inspections ?? []) {
      if (row.property_state) values.add(row.property_state);
      else hasBlank = true;
    }
    const options = Array.from(values, (value) => ({
      value,
      label: value,
    })).sort((a, b) => a.label.localeCompare(b.label));
    if (hasBlank) options.push({ value: "none", label: "Unknown" });
    return options;
  }, [inspections]);

  // Record-metadata facets in the filter fly-out. Status + inspector +
  // scheduled/created date presets always available; type + result depend on
  // the tenant's configured choices.
  const filterFacets: ProductFilterFacet[] = useMemo(() => {
    const facets: ProductFilterFacet[] = [];
    if (choices?.lifecycle_statuses?.length) {
      facets.push({
        key: "status",
        label: "Status",
        kind: "field",
        options: choices.lifecycle_statuses,
      });
    }
    if (inspectorOptions.length) {
      facets.push({
        key: "inspector",
        label: "Inspector",
        kind: "field",
        options: inspectorOptions,
      });
    }
    if (stateOptions.length) {
      facets.push({
        key: "state",
        label: "State",
        kind: "field",
        options: stateOptions,
      });
    }
    facets.push({
      key: "scheduled",
      label: "Scheduled",
      kind: "field",
      type: "date",
      options: SCHEDULED_PRESETS,
    });
    facets.push({
      key: "created",
      label: "Created",
      kind: "field",
      type: "date",
      options: CREATED_PRESETS,
    });
    if (subTypes?.length) {
      facets.push({
        key: "sub_type",
        label: "Type",
        kind: "field",
        options: subTypes.map((st) => ({ value: st.key, label: st.label })),
      });
    }
    if (choices?.results?.length) {
      facets.push({
        key: "result",
        label: "Result",
        kind: "field",
        options: choices.results,
      });
    }
    return facets;
  }, [subTypes, choices, inspectorOptions, stateOptions]);

  const hasFilters = Boolean(
    statusFilter ||
    term ||
    bucket !== "all" ||
    subTypeSelection.length ||
    resultSelection.length ||
    statusSelection.length ||
    inspectorSelection.length ||
    stateSelection.length ||
    createdPreset ||
    scheduledPreset,
  );
  const showEmptyState =
    !isLoading &&
    !isFetching &&
    (inspections?.length ?? 0) === 0 &&
    !hasFilters;

  const columns: DataTableColumn<InspectionRow>[] = useMemo(
    () => [
      {
        id: "code",
        header: "Code",
        accessorKey: "code",
        cell: (row) => (
          <span className="inline-flex items-center gap-2">
            {row.spotlight?.active ? (
              <NotificationDot
                level={row.spotlight.level}
                title={row.spotlight.title || "Needs attention"}
              />
            ) : null}
            <span
              className={cn(
                "font-mono text-[12px]",
                // New-inspection emphasis: darken + bolden the code for a
                // freshly-created, not-yet-scheduled inspection (spotlight.is_new).
                // Clears when the "awaiting scheduling" notification is read or the
                // inspection leaves its initial state. Distinct from the attention
                // dot (any unread), so unread results/assignments don't bold.
                row.spotlight?.is_new
                  ? "font-semibold text-[var(--ink)]"
                  : "text-[var(--ink-3)]",
              )}
            >
              {row.code}
            </span>
            {row.is_priority && (
              <Badge
                variant="destructive"
                className="px-1.5 py-0 text-[10px] uppercase tracking-[0.08em]"
                title={row.priority_reason || "Marked priority"}
              >
                Priority
              </Badge>
            )}
          </span>
        ),
      },
      {
        // Deliberately its own column rather than folded into the code cell:
        // Source (where the work came from) and Priority (the team saying it
        // is urgent) are independent axes, so the flag has to be settable
        // without touching anything else on the row.
        id: "priority",
        header: "Priority",
        accessorKey: "is_priority",
        cell: (row) =>
          canMarkPriority ? (
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              title={row.is_priority ? "Remove priority" : "Mark priority"}
              onClick={(e) => {
                e.stopPropagation();
                markPriority.mutate({
                  inspection_uuids: [row.uuid],
                  is_priority: !row.is_priority,
                });
              }}
            >
              <Flag
                size={13}
                className={
                  row.is_priority
                    ? "fill-[var(--status-error)] text-[var(--status-error)]"
                    : "text-[var(--ink-3)]"
                }
              />
            </Button>
          ) : row.is_priority ? (
            <Flag
              size={13}
              className="fill-[var(--status-error)] text-[var(--status-error)]"
            />
          ) : null,
      },
      {
        id: "sub_type",
        header: "Type",
        accessorKey: "sub_type_label",
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span>{row.sub_type_label}</span>
            {/* What KIND of claim caused this, and what is wrong. "Quality
                Claim Inspection" alone does not distinguish shingles curling
                on a house from a dealer's stock. */}
            {row.claim_context?.claim_type_label && (
              <span className="text-[10px] text-[var(--ink-3)]">
                {row.claim_context.claim_type_label}
                {row.claim_context.defect
                  ? ` \u00b7 ${row.claim_context.defect}`
                  : ""}
              </span>
            )}
          </div>
        ),
      },
      // An inspector picking work off this queue needs to know what the job
      // is attached to. Without it the queue is a wall of INSP codes and the
      // only way to find out is to open each one.
      {
        id: "claim",
        header: "Claim",
        accessorKey: "claim_ref",
        cell: (row) =>
          row.claim_ref && row.claim_uuid ? (
            <div className="flex flex-col items-start gap-0.5">
              <Button
                variant="link"
                className="h-auto p-0 font-mono text-[12px]"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/claims/${row.claim_uuid}`);
                }}
              >
                {row.claim_ref}
              </Button>
              {/* The claim's own service level, so an inspector reads the
                  deadline they are working to straight off the queue. Job
                  Stop runs a 7 day SLA; a workflow that declares none shows
                  nothing rather than an invented default. */}
              {row.claim_service_level?.is_priority &&
              row.claim_service_level?.sla_days != null ? (
                <span className="inline-flex items-center gap-1 rounded-lg border-[0.5px] border-red-700 bg-red-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-red-700">
                  {t("inspections.list.daySla", {
                    defaultValue: "{{count}} Day SLA",
                    count: row.claim_service_level.sla_days,
                  })}
                </span>
              ) : null}
            </div>
          ) : (
            <span className="text-[var(--ink-3)]">-</span>
          ),
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "lifecycle_status",
        cell: (row) => <StatusBadge status={row.lifecycle_status} />,
      },
      {
        id: "result",
        header: "Result",
        accessorKey: "result",
        cell: (row) =>
          row.result ? (resultLabels[row.result] ?? row.result) : "-",
      },
      {
        id: "scheduled_for",
        header: "Scheduled",
        accessorKey: "scheduled_for",
        cell: (row) =>
          row.scheduled_for ? formatDate(row.scheduled_for) : "-",
      },
      {
        id: "inspector",
        header: "Inspector",
        accessorKey: "inspector_name",
        cell: (row) => row.inspector_name || "-",
      },
      {
        id: "contractor",
        header: "Contractor",
        accessorKey: "contractor_name",
        cell: (row) => row.contractor_name || "-",
      },
      {
        id: "property",
        header: "Property",
        cell: (row) => formatPropertyAddress(row),
      },
      {
        id: "state",
        header: "State",
        accessorKey: "property_state",
        cell: (row) => row.property_state || "-",
      },
      {
        id: "evidence",
        header: "Evidence",
        accessorKey: "evidence_count",
        cell: (row) =>
          row.evidence_count > 0 ? `${row.evidence_count} files` : "-",
      },
    ],
    [resultLabels, formatDate, canMarkPriority, markPriority, navigate, t],
  );

  const buildCurrentConfig = useCallback(
    (
      overrides: {
        columnVisibility?: Record<string, boolean>;
        columnOrder?: string[];
      } = {},
    ): TableViewConfig => {
      const nextColumnVisibility =
        overrides.columnVisibility ?? columnVisibility;
      const nextColumnOrder = overrides.columnOrder ?? columnOrder;
      const visibleIds = columns
        .filter((col) => {
          if (col.id in nextColumnVisibility)
            return nextColumnVisibility[col.id] !== false;
          return col.defaultVisible !== false;
        })
        .map((col) => col.id);
      return {
        columns: visibleIds,
        columnOrder: nextColumnOrder,
        sorting: null,
        filters: [],
      };
    },
    [columns, columnVisibility, columnOrder],
  );

  const currentConfig = buildCurrentConfig();

  const handleSelectView = useCallback(
    (view: TableView | null) => {
      setActiveViewUuid(view?.uuid ?? null);
      if (view?.config) {
        if (view.config.columns) {
          const vis: Record<string, boolean> = {};
          columns.forEach((c) => {
            vis[c.id] = view.config.columns!.includes(c.id);
          });
          setColumnVisibility(vis);
        } else {
          setColumnVisibility({});
        }
        setColumnOrder(view.config.columnOrder ?? []);
      } else {
        applyDefaultColumnPreferences();
      }
    },
    [
      applyDefaultColumnPreferences,
      columns,
      setColumnOrder,
      setColumnVisibility,
    ],
  );

  return (
    <AppShell>
      <main className="page-container py-10 flex flex-col gap-6">
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="clara-title">Inspections</h1>
            <p className="mt-1 text-[12px] text-[var(--ink-3)]">
              {hasFilters
                ? `${rows.length} of ${inspections?.length ?? 0} inspection${
                    (inspections?.length ?? 0) === 1 ? "" : "s"
                  }`
                : `${inspections?.length ?? 0} inspection${
                    (inspections?.length ?? 0) === 1 ? "" : "s"
                  } for this organisation`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 self-start sm:self-auto sm:mb-1">
            {/* Smart Schedule entry points. Their routes gate on `scheduling`,
                so hide the buttons with the same flag - otherwise they navigate
                straight into a redirect. */}
            <FeatureGate flag={FLAGS.SCHEDULING}>
              <Button
                variant="outline"
                onClick={() => navigate("/inspections/queue")}
                className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
              >
                <ListChecks size={13} className="mr-1.5" />
                Pool
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/inspections/queue?tab=itineraries")}
                className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
              >
                <Route size={13} className="mr-1.5" />
                Itineraries
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/inspections/scheduling")}
                className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
              >
                <CalendarDays size={13} className="mr-1.5" />
                Scheduling
              </Button>
            </FeatureGate>
            <Button
              variant="outline"
              onClick={() => navigate("/inspections/punch-list")}
              className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
            >
              <Wrench size={13} className="mr-1.5" />
              {t("inspections.landing.punchList", "Punch list")}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/inspections/dashboard")}
              className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
            >
              <BarChart3 size={13} className="mr-1.5" />
              Dashboard
            </Button>
            {canCreate && (
              <Button
                onClick={() => navigate("/inspections/new")}
                className="rounded-[4px] px-4 text-[11px] font-normal uppercase tracking-[0.1em] bg-[var(--green)] hover:bg-[var(--green-hover)] text-white border-none"
              >
                + New Inspection
              </Button>
            )}
          </div>
        </header>

        {showEmptyState ? (
          <EmptyState
            icon={ClipboardCheck}
            title="No inspections yet"
            description={
              "Inspections will appear here once you create one manually or a rule auto-creates one from a claim or registration."
            }
            action={
              canCreate
                ? {
                    label: "New Inspection",
                    onClick: () => navigate("/inspections/new"),
                  }
                : undefined
            }
          />
        ) : (
          <>
            {/* Priority | Pool split - two worlds of work */}
            <Tabs
              value={bucket}
              onValueChange={(v: string) =>
                setBucket(v as "all" | "priority" | "pool")
              }
            >
              <TabsList variant="pill" className="w-fit">
                <TabsTrigger value="all">
                  {t("inspections.landing.tabAll", "All")}
                </TabsTrigger>
                <TabsTrigger value="priority">
                  {t("inspections.landing.tabPriority", "Priority")}
                </TabsTrigger>
                <TabsTrigger value="pool">
                  {t("inspections.landing.tabPool", "Pool")}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <ListPageToolbar<InspectionRow>
              layout="compact"
              tableId={TABLE_ID}
              search={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search code, property, contractor…"
              statusPills={statusPills}
              activeStatusKey={statusFilter}
              onStatusSelect={setStatusFilter}
              statusTotalCount={preStatusRows.length}
              views={views}
              activeViewUuid={activeViewUuid}
              onSelectView={handleSelectView}
              onSaveView={(name) =>
                createView.mutate({
                  table_id: TABLE_ID,
                  name,
                  config: currentConfig,
                })
              }
              onDeleteView={(uuid) => {
                deleteView.mutate({ uuid, tableId: TABLE_ID });
                if (activeViewUuid === uuid) setActiveViewUuid(null);
              }}
              onUpdateView={(uuid) =>
                updateView.mutate({
                  uuid,
                  tableId: TABLE_ID,
                  config: currentConfig,
                })
              }
              onRenameView={(uuid, name) =>
                updateView.mutate({ uuid, tableId: TABLE_ID, name })
              }
              onSetOrgDefault={() =>
                setOrgDefault.mutate({
                  table_id: TABLE_ID,
                  name: "Default",
                  config: currentConfig,
                })
              }
              currentConfig={currentConfig}
              columns={columns}
              activeFilters={[]}
              onFiltersChange={() => {}}
              showColumnToggle={false}
              filterFacets={filterFacets}
              filterValues={filterValues}
              onFilterValuesChange={setFilterValues}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={(visibility) => {
                setColumnVisibility(visibility);
                if (activeViewUuid) {
                  updateView.mutate({
                    uuid: activeViewUuid,
                    tableId: TABLE_ID,
                    config: buildCurrentConfig({
                      columnVisibility: visibility,
                    }),
                  });
                } else {
                  saveColumnVisibility(visibility);
                }
              }}
              alwaysShownColumns={["code", "status"]}
              columnOrder={columnOrder}
              onColumnOrderChange={(order) => {
                setColumnOrder(order);
                if (activeViewUuid) {
                  updateView.mutate({
                    uuid: activeViewUuid,
                    tableId: TABLE_ID,
                    config: buildCurrentConfig({ columnOrder: order }),
                  });
                } else {
                  saveColumnOrder(order);
                }
              }}
            />

            <DataTable<InspectionRow>
              tableId={TABLE_ID}
              columns={columns}
              data={rows}
              isLoading={isLoading}
              isFetching={isFetching}
              isError={isError}
              onRetry={refetch}
              onRowClick={(row) => navigate(`/inspections/${row.uuid}`)}
              rowKey="uuid"
              emptyMessage="No inspections match your filters."
              columnVisibility={columnVisibility}
              columnOrder={columnOrder}
            />
          </>
        )}
      </main>
    </AppShell>
  );
}
